<template lang="pug">
    .authenticated(v-if="user && user.id" v-noautofill :class="{ 'is-dev': isDevelop }")
        el-container.app-wrap(:class="{ 'sidebar-visible': showSidebar, 'sidebar-ready': sidebarReady }")
            el-header(height="auto" :class="{ 'no-cms': (!cms || !cms.id) && !isSuperUser }")
                //- BRANDING
                .logo
                    router-link(:to="{ name: 'dashboard' }")
                        progressive-img(:src="require('@/assets/CObyVA.svg')" :aspect-ratio="0.46")
                el-row.header__setting.row-bg(type="flex")
                    .col.sidebar-button
                        el-tooltip(:content="sidebarToggleTooltip" placement="bottom")
                            .button__toggle(@click="toggleSidebar")
                                span
                    .col.inline-selection.left-buttons
                        el-dropdown.mobile-only.action-menu(trigger="click" placement="bottom" @command="handleActionCommand")
                            el-button.moderate(
                                circle
                                size="mini"
                                type="info"
                                icon="el-icon-fa-ellipsis-vertical fas"
                            )
                            el-dropdown-menu(slot="dropdown")
                                el-dropdown-item(
                                    v-for="cmsItem in cmss"
                                    :key="cmsItem.id"
                                    :command="'switchCms:' + cmsItem.id"
                                    :class="{ 'is-active': cms && cms.id === cmsItem.id }"
                                ) {{ cmsItem.name }}
                                el-dropdown-item(divided command="addWebProject")
                                    i.el-icon-fa-plus.fas
                                    |  {{ $t('Add Web Project') }}
                                el-dropdown-item(v-if="checkRestrictions('settings')" command="settings")
                                    i.el-icon-fa-cog.fas
                                    |  {{ $t('Settings') }}
                                el-dropdown-item(v-if="cms && cms.id" command="clearCache")
                                    i.el-icon-fa-recycle.fas
                                    |  {{ $t('Clear Cache') }}
                                el-dropdown-item(v-if="showsPlatformTickets" command="platformTickets")
                                    i.el-icon-fa-headset.fas
                                    |  {{ $t('Support') }}
                                    el-badge.platform-tickets-badge--inline(v-if="tenantPlatformUnreadCount" :value="tenantPlatformUnreadCount" :max="99")
                        .select.desktop-only(v-if="cmss && cmss.length >= 1")
                            .dynamic-width-field
                                .occupy-width {{ cms.name }}
                                el-select(placeholder="" v-model="cms" valueKey="id" size="mini")
                                    el-option(v-for="cms in cmss" :key="cms.id" :label="cms.name" :value="cms")
                        //- No permission gate: whoever completes the wizard becomes the owner
                        //- of the CMS they create (InitializeCms falls back to the authenticated
                        //- user, Cms::setOwner creates the owner administrator). Gating on
                        //- isOwner was circular - it reads administrator.owner of the ACTIVE
                        //- cms, so a super-user without a cms could never reach the wizard.
                        el-tooltip.desktop-only(:content="$t('Add Web Project')" placement="bottom")
                            el-button.settings-button.moderate(
                                circle
                                size="mini"
                                type="info"
                                icon="el-icon-fa-plus fas"
                                @click.stop.prevent="goToNewAccount"
                            )
                        el-tooltip.desktop-only(v-if="checkRestrictions('settings')" :content="$t('Settings')" placement="bottom")
                            el-button.settings-button.moderate(
                                circle
                                size="mini"
                                type="info"
                                icon="el-icon-fa-cog fas"
                                @click.stop.prevent="goToSettings"
                            )
                        el-tooltip.desktop-only(v-if="cms && cms.id" :content="$t('Clear Cache')" placement="bottom")
                            el-button.clear-cache-button.moderate(
                                circle
                                size="mini"
                                type="info"
                                icon="el-icon-fa-recycle fas"
                                :loading="clearingCache"
                                @click.stop.prevent="clearCache"
                            )
                        //- Requests to US about this website (T-350), not the
                        //- shop's own helpdesk under /tickets.
                        //- The badge counts tickets with a reply of ours not opened yet.
                        el-tooltip.desktop-only(v-if="showsPlatformTickets" :content="$t('Support')" placement="bottom")
                            el-badge.platform-tickets-badge(:value="tenantPlatformUnreadCount" :max="99" :hidden="!tenantPlatformUnreadCount")
                                //- Styled like the "back to" button of a mimic: this one is
                                //- ours too, a way to reach us, not a tool of the website.
                                el-button.moderate(
                                    circle
                                    size="mini"
                                    type="warning"
                                    icon="el-icon-fa-headset fas"
                                    @click.stop.prevent="platformTicketsVisible = true"
                                )
                        template(
                            v-if="microserviceButtons && microserviceButtons.left && microserviceButtons.left.length"
                            v-for="microservice in microserviceButtons.left"
                        )
                            MicroserviceButton(
                                :data="microserviceData"
                                :config="microservice"
                                button-class="moderate"
                                appearance="circle"
                                size="mini"
                            )
                    portal-target.header-left(name="header-left" slim)
                    portal-target.header-center(name="header-center" slim)
                        .grow
                    //- A running session is otherwise invisible until you leave
                    //- the account. One pill per desk: a super user who also
                    //- administers a shop runs OUR support timer and THEIR
                    //- helpdesk timer at the same time, and inside a mimic the
                    //- two are ours and the mimicked account's.
                    .col.support-time-col
                        support-time-tracker(v-if="canTrackPlatformTime" desk="platform")
                        support-time-tracker(v-if="trackedCmsId" desk="tenant" :cms-id="trackedCmsId")
                    .col.textright.desktop-only(v-if="isDevelop")  DEV
                    portal-target.header-right(name="header-right" slim)
                    .col.inline-selection.right-buttons
                        template(
                            v-if="microserviceButtons && microserviceButtons.right && microserviceButtons.right.length"
                            v-for="microservice in microserviceButtons.right"
                        )
                            MicroserviceButton(
                                :data="microserviceData"
                                :config="microservice"
                                appearance="circle"
                                size="mini"
                            )
                    .col.header-menu.textright
                        .stop-mimic(v-if="isImpersonating")
                            el-tooltip(:content="$t('back to') + ' ' + impersonator.first_name + ' ' + impersonator.last_name" placement="bottom")
                                el-button.moderate(
                                    circle
                                    size="mini"
                                    type="warning"
                                    icon="el-icon-fa-arrow-right-from-bracket fas"
                                    @click="stopMimic"
                                )
                        .translation
                            el-dropdown(trigger="click" placement="bottom" @command="changeTranslation")
                                a.language-trigger(href="#" @click.prevent)
                                    | {{ locale ? locale.value.toUpperCase() : '' }}
                                    i.el-icon-arrow-down.el-icon--right
                                el-dropdown-menu(slot="dropdown")
                                    el-dropdown-item(v-for="item in i18n" :key="item.value" :command="item.value")
                                        span.circle.fi.fis(:class="`fi-${item.flag}`")
                                        | {{ $t(item.label) }}
                        .user-dropdown(v-if="user")
                            el-dropdown(trigger="click" placement="bottom" @command="handleUserCommand")
                                a.user-trigger(href="#" @click.prevent)
                                    span.mobile
                                        i.el-icon-fa-user.fas
                                    span.desktop {{ user.first_name }} {{ user.last_name }}
                                    i.el-icon-arrow-down.el-icon--right
                                el-dropdown-menu(slot="dropdown")
                                    el-dropdown-item(command="account")
                                        i.el-icon-fa-user.fas
                                        |  {{ $t('Account') }}
                                    el-dropdown-item(v-if="canAccessBilling" command="billing")
                                        i.el-icon-fa-file-invoice-dollar.fas
                                        |  {{ $t('Billing & costs') }}
                                    el-dropdown-item(command="logout")
                                        i.el-icon-fa-right-from-bracket.fas
                                        |  Logout
            el-container.Menu(:class="{ 'Menu__Mobile_open': showSidebar && isMobileViewport }")
                //- A super-user has no active CMS of their own — render the sidebar for
                //- them anyway so the "CityOffer Admin → Accounts" entry is reachable and
                //- they can mimic into a tenant. (When impersonating, cms.id is set.)
                el-aside#sticky-aside-container(v-if="(cms && cms.id) || isSuperUser")
                    //- NAVIGATION
                    affix.sticky-stuff(
                        relative-element-selector="#sticky-aside-container"
                        :offset="{ top: 0, bottom: 0 }"
                        :scroll-affix="true"
                    )
                        navigation(:expanded="showSidebar")
                el-container
                    el-main.main-slot
                        CmsDisabledBanner
                        MissingEmailSenderBanner
                        SharedShopDataBanner
                        slot
                    //- Full-height side panels of a page (the dashboard's widget drawer):
                    //- a sibling of the main area, so the banners above the page never push it down
                    portal-target(name="main-aside" slim)
                    // el-footer
                    //     | Footer
        //- .unfinished-jobs
        //-     transition-group(name="fade" mode="in-out")
        //-         JobMessage(
        //-             v-for="job in jobs"
        //-             v-if="!job.hidden && job.notify"
        //-             :key="job.id"
        //-             :job="job"
        //-         )

        //- mimic support-time tracking (super users): the pre-mimic choice and
        //- the apply-time dialog on leaving
        start-timer-dialog
        stop-support-time-dialog
        platform-tickets-drawer(v-if="showsPlatformTickets" :visible.sync="platformTicketsVisible" :cms-id="cms.id")

    .sign-in(v-else)
        transition(name="slide-fade" mode="out-in")
            SignIn(v-if="loaded")
</template>

<i18n>
{
    "de": {
        "Web Project": "Webauftritt",
        "Cache could not be cleared": "Cache konnte nicht gelöscht werden",
        "Cache cleared successfully": "Cache erfolgreich gelöscht",
        "back to": "zurück zu",
        "user interface": "Benutzeroberfläche",
        "German": "Deutsch",
        "English": "Englisch",
        "Add Web Project": "Webauftritt hinzufügen",
        "Settings": "Einstellungen",
        "Clear Cache": "Cache leeren",
        "Account": "Konto",
        "Billing & costs": "Abrechnung & Kosten",
        "Show Sidebar": "Menü ausklappen",
        "Hide Sidebar": "Menü einklappen"
    }
}
</i18n>

<script>
import _ from 'lodash';

import {
    useAccountStore,
    useAuthStore,
    useSettingsStore,
    useSetupStore,
    usePosSetupStore,
    useTicketStore,
} from '@/stores';
import { checkRestrictions } from '@/helpers';
import { language, languageFlag, languageNativeName } from '@/filters';

import PointOfSaleApi from '@/extensions/shop/pointOfSale/store/api/pointOfSale';

import Navigation from '@/components/Navigation';
import SignIn from '@/components/SignIn';
import StartTimerDialog from '@/extensions/tickets/components/StartTimerDialog';
import SupportTimeTracker from '@/extensions/tickets/components/SupportTimeTracker';
import StopSupportTimeDialog from '@/extensions/tickets/components/StopSupportTimeDialog';
import PlatformTicketsDrawer from '@/extensions/tickets/components/PlatformTicketsDrawer';
// import I18nSwitcher from '@/components/I18nSwitcher'
import MicroserviceButton from '@/components/MicroserviceButton';
import CmsDisabledBanner from '@/components/CmsDisabledBanner';
import MissingEmailSenderBanner from '@/components/MissingEmailSenderBanner';
import SharedShopDataBanner from '@/components/SharedShopDataBanner';
// import JobMessage from '@/components/JobMessage';

import { adminClient as apolloClient } from '@/store/apollo';
// eslint-disable-next-line
import router from '@/router';

const SIDEBAR_STATE_COOKIE = 'backend_ui_sidebar_expanded';

export default {
    name: 'Auth',
    data() {
        return {
            showSidebar: false,
            clearingCache: false,
            platformTicketsVisible: false,
            sidebarReady: false,
            isMobileViewport: false,
        };
    },
    components: {
        Navigation,
        SignIn,
        MicroserviceButton,
        CmsDisabledBanner,
        MissingEmailSenderBanner,
        SharedShopDataBanner,
        StartTimerDialog,
        SupportTimeTracker,
        StopSupportTimeDialog,
        PlatformTicketsDrawer,
        // JobMessage,
        // I18nSwitcher
    },
    watch: {
        // The Tickets badge follows the selected website.
        showsPlatformTickets: {
            immediate: true,
            handler() {
                this.syncTenantPlatformTickets();
            },
        },
        'cms.id'() {
            this.syncTenantPlatformTickets();
        },
        $route(to, from) {
            if (to.path !== from.path && this.isMobileViewport) {
                this.showSidebar = false;
            }
        },
        user: {
            immediate: true,
            handler(newUser) {
                const authStore = useAuthStore();
                // When user becomes unauthenticated, store the current route for redirect after login
                if (!newUser || !newUser.id) {
                    const currentPath = this.$route.fullPath;
                    // Don't store if we're already on dashboard or a public route
                    if (currentPath !== '/' && currentPath !== '/dashboard') {
                        authStore.setRedirectAfterLogin(currentPath);
                    }
                }
            },
        },
    },
    computed: {
        // jobs() {
        //     return this.allJobs.filter(job => {
        //         return job.notify === true && _.get(job, 'user.id') == this.user.id
        //     })
        // },
        cms: {
            get() {
                return this.activeCms;
            },
            set(cms) {
                apolloClient.clearStore().then(() => {
                    // set the reload flag to false
                    // so the compenents will not get triggered twice
                    // firstly on this activeCms chage and
                    // secondly when the setup state is getting replaced
                    cms.reloadComponents = false;

                    this.updateActiveCms(cms);

                    useSetupStore().updateLoadedState(false);
                    this.fetchSetup().finally(() => {
                        // TODO: check if the current route exists
                        // if not, redirect to the most sane alternative

                        useSetupStore().updateLoadedState(true);
                    });
                });
            },
        },
        isDevelop() {
            let local = false;
            if (process.env.NODE_ENV === 'development') local = true;
            return local;
        },
        microserviceData() {
            let activePointOfSale;
            if (this.enabledExtensions.includes('shop') && this.enabledExtensions.includes('shop:point-of-sale')) {
                activePointOfSale = this.activePointOfSale;
                if (!activePointOfSale || !activePointOfSale.key) {
                    activePointOfSale = {
                        id: PointOfSaleApi.getActivePointOfSaleId(),
                    };
                }
            }
            return {
                activeCms: this.activeCms,
                activePointOfSale,
            };
        },
        microservices() {
            const all = _.get(this.activeCms, 'config.template.microservices') || [];
            const filtered = all.filter((el) => el.component === 'Header');

            // eslint-disable-next-line no-unused-vars
            const data = this.microserviceData;

            let allowed = [];
            filtered.forEach((microservice) => {
                let valid = true;
                try {
                    const conditions = microservice.conditions;
                    if (conditions && conditions.length) {
                        conditions.forEach((o) => {
                            if (valid === false) return;

                            const { type, condition } = o;
                            switch (type) {
                                case 'eval':
                                    valid = eval(condition);
                                    break;

                                default:
                                    valid = false;
                            }
                        });
                    }
                } catch (error) {
                    console.error(error);
                    valid = false;
                }

                if (valid) allowed.push(microservice);
            });

            return allowed;
        },
        microserviceButtons() {
            let microservices = {};

            this.microservices.forEach((microservice) => {
                if (microservice.button) {
                    const position = _.get(microservice, 'button.position') || 'left';
                    if (!microservices[position]) microservices[position] = [];
                    microservices[position].push(microservice);
                }
            });

            return microservices;
        },
        // Auth store (Pinia)
        user() {
            return useAuthStore().user;
        },
        // Both from the server's word on the Setup payload - whether this
        // browser is inside a support session, and who really entered.
        // The selected website's requests to US (T-350).
        showsPlatformTickets() {
            return !!(this.cms && this.cms.id);
        },
        tenantPlatformUnreadCount() {
            return useTicketStore().tenantPlatformUnreadCount;
        },
        isImpersonating() {
            return useAuthStore().isImpersonating;
        },
        impersonator() {
            return useAuthStore().impersonator;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        isImpersonatedBySuperUser() {
            return useAuthStore().isImpersonatedBySuperUser;
        },
        canAccessBilling() {
            // Temporarily restricted to super-users (or super-users impersonating
            // another admin) until billing is rolled out to all owners.
            if (!this.cms || !this.cms.id) return false;
            return this.isSuperUser || this.isImpersonatedBySuperUser;
        },
        // Our own desk is ours, and it stays ours inside a customer's account:
        // during a session the account user is the CUSTOMER, so the server's
        // word on who really entered is what says the platform pill still
        // belongs here.
        canTrackPlatformTime() {
            return this.isSuperUser || this.isImpersonatedBySuperUser;
        },
        // The shop whose helpdesk timer the second pill is for. Gated the same
        // way the tickets menu entry is: no tickets feature, no tenant pill.
        trackedCmsId() {
            const id = _.get(this.activeCms, 'id');
            const hasTickets =
                this.enabledExtensions.includes('tickets') && checkRestrictions(['ex:tckt', 'ex:tckt:tickets'])
                // Everything the pill does (start, pause, stop, book time)
                // is the update flag.
                && checkRestrictions(['ex:tckt:tickets'], 'update');

            return id && hasTickets ? parseInt(id, 10) : null;
        },
        // Setup store (Pinia)
        loaded() {
            return useSetupStore().loaded;
        },
        i18n() {
            return useSetupStore().i18n;
        },
        locale() {
            return useSetupStore().localeObject;
        },
        cmss() {
            return useSetupStore().cmss;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        posSetupStore() {
            return usePosSetupStore();
        },
        activePointOfSale() {
            return this.posSetupStore.activePointOfSale;
        },
        sidebarToggleTooltip() {
            return this.showSidebar ? this.$t('Hide Sidebar') : this.$t('Show Sidebar');
        },
    },
    methods: {
        syncTenantPlatformTickets() {
            const ticketStore = useTicketStore();

            if (this.showsPlatformTickets) ticketStore.subscribeTenantPlatformTickets(this.cms.id);
            else ticketStore.unsubscribeTenantPlatformTickets();
        },
        getLanguageName(code) {
            return language(code, this.locale ? this.locale.value : 'de');
        },
        getLanguageFlag(code) {
            return languageFlag(code);
        },
        getLanguageNativeName(code) {
            return languageNativeName(code);
        },
        changeTranslation(code) {
            // this.$root.$i18n.locale = code
            this.switchI18n(code);
        },
        handleSignout(event) {
            event.preventDefault();
            useAuthStore().signout();
        },
        getCookie(name) {
            if (typeof document === 'undefined') return '';

            const escapedName = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
            return match ? decodeURIComponent(match[1]) : '';
        },
        setCookie(name, value, days = 365) {
            if (typeof document === 'undefined') return;

            const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
            document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
        },
        getStoredSidebarState() {
            return this.getCookie(SIDEBAR_STATE_COOKIE) === '1';
        },
        persistSidebarState(isOpen) {
            this.setCookie(SIDEBAR_STATE_COOKIE, isOpen ? '1' : '0');
        },
        applySidebarStateForViewport() {
            if (this.isMobileViewport) {
                this.showSidebar = false;
                return;
            }

            this.showSidebar = this.getStoredSidebarState();
        },
        toggleSidebar() {
            this.showSidebar = !this.showSidebar;

            if (!this.isMobileViewport) {
                this.persistSidebarState(this.showSidebar);
            }
        },
        clearCache() {
            this.clearingCache = true;
            this.clearCacheForCms(this.activeCms)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Cache cleared successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    this.$notify.error({
                        title: this.$t('Cache could not be cleared'),
                        // message: error.message,
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .finally(() => {
                    this.clearingCache = false;
                });
        },
        goToOwnAccount() {
            // reset account store
            useAccountStore().reset();
            useAccountStore().resetUI();
            useAccountStore().setFormInitialized(false);
            // redirect to route '/account'
            router.push({ name: 'system-account-form', params: { id: undefined } });
        },
        goToSettings() {
            //     // reset account store
            useSettingsStore().reset();
            useSettingsStore().setFormInitialized(false);
            //     // redirect to route '/settings'
            router.push({ name: 'settings-form', params: { id: undefined } });
        },
        goToNewAccount() {
            // Redirect to CMS wizard for creating new CMS
            router.push({ name: 'cms-setup-wizard' });
        },
        checkRestrictions,
        handleActionCommand(command) {
            if (command === 'addWebProject') this.goToNewAccount();
            else if (command === 'settings') this.goToSettings();
            else if (command === 'clearCache') this.clearCache();
            else if (command === 'platformTickets') this.platformTicketsVisible = true;
            else if (command && command.startsWith('switchCms:')) {
                var cmsId = parseInt(command.split(':')[1]);
                var selectedCms = this.cmss.find(function (item) {
                    return item.id === cmsId;
                });
                if (selectedCms) this.cms = selectedCms;
            }
        },
        handleUserCommand(command) {
            if (command === 'account') this.goToOwnAccount();
            else if (command === 'billing') router.push({ name: 'system-account-billing' });
            else if (command === 'logout') useAuthStore().signout();
        },
        async stopMimic() {
            // Leave FIRST: the server closes the session and hands back our own
            // token, so the stop mutation below runs as ourselves - which is
            // whose entry it is. Then ask what to do with the tracked time (if
            // this session tracked any). Still inside because leaving failed:
            // nothing below applies, the session simply goes on.
            if (! await useAuthStore().stopMimic()) return;

            // Their own timer was started AS them and keeps running on their
            // books; we cannot act on it from out here, so the pill pointing at
            // it goes. Ours is what the dialog below is about to ask about.
            useTicketStore().forgetMimicTracking();

            // Only a timer started AS the pre-mimic choice is this session's:
            // one that was already running on some unrelated internal ticket
            // when the mimic began (entered "ohne Erfassung") has nothing to
            // do with the account just left and simply keeps running. And the
            // question is a question: "Weiterlaufen lassen" is an answer too.
            const stash = useTicketStore().platformTrackingStash;

            if (stash && stash.mimic) {
                useTicketStore().openStopDialog('platform', { dismissible: true, leaving: true });
            }
        },
        updateActiveCms(cms) {
            useSetupStore().updateActiveCms(cms);
        },
        switchI18n(code) {
            useSetupStore().switchI18n(code);
        },
        fetchSetup(ignoreCmsID) {
            return useSetupStore().fetchSetup(ignoreCmsID);
        },
        clearCacheForCms(cms) {
            return useSetupStore().clearCacheForCms(cms);
        },
    },
    mounted() {
        this.handleResize = () => {
            this.isMobileViewport = window.innerWidth < 1200;
            this.applySidebarStateForViewport();
        };

        window.addEventListener('resize', this.handleResize);
        this.handleResize();

        // A new socket (entering or leaving a mimic) is not subscribed yet.
        document.addEventListener('echo:connected', this.syncTenantPlatformTickets);

        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.sidebarReady = true;
            });
        });
    },
    beforeDestroy() {
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize);
        }
        document.removeEventListener('echo:connected', this.syncTenantPlatformTickets);
        useTicketStore().unsubscribeTenantPlatformTickets();
    },
};
</script>

<style lang="sass" nolang="scss" scoped>

@import "~flag-icons/css/flag-icons.min.css"

$--button-hover-tint-percent: 5%
@import "~element-ui/packages/theme-chalk/src/mixins/_button.scss"

$sidebar-collapsed-width: 110px
$sidebar-expanded-width: 240px

.el-container.app-wrap
    --sidebar-width: #{$sidebar-collapsed-width}
    --sidebar-background: #ffffff
    --sidebar-border: #e7ebf1
    --sidebar-shadow: none
    min-height: 100vh
    &.sidebar-visible
        --sidebar-width: #{$sidebar-expanded-width}
        --sidebar-background: #ffffff
        --sidebar-border: #dfe6ee
        --sidebar-shadow: 8px 0 20px rgba(15, 23, 42, 0.06)


.v-modal
    transform: translateX(0)
.el-dialog
    width: 100%
    max-width: 590px
.el-main.main-slot
    // height: 100%
    // min-height: 100vh
    display: flex
    flex-direction: column
    overflow: visible // quill tooltip needs this
    min-width: 0
    h1
        margin: 0
    h4
        margin-top: 10px
        margin-bottom: 0
    .el-main
        .el-tab-pane
            .el-row
                &.tab_content_header
                    padding-top: 0
    :deep() > .Main__view
        min-height: 100%
        width: 100%
        // max-width: 1920px
        max-width: 2304px
        margin: 0 auto

    .full_size_btn
        width: 100%
    .el-row
        padding-top: 15px

    // :deep()
    //     .el-form .el-card .el-card__body section > .el-row > .el-col-lg-12:first-child
    //         position: relative
    //         &:before
    //             display: none
    //             content: ''
    //             position: absolute
    //             top: 0
    //             right: -1.5px
    //             height: 100%
    //             border-right: 3px dotted $--color-primary
    //             +res(lg)
    //                 display: initial

// Its own visibility rather than .desktop-only, whose `display: initial
// !important` would beat the flex box that centres the pill in the 60px bar.
// A clock that is RUNNING must not be invisible: it was hidden outright below
// lg, so on a laptop the only sign of a session was the bill. The pills stay
// at every width and drop their labels instead.
.el-header .support-time-col
    display: flex
    align-items: center
    gap: 6px
    margin-right: 12px

.el-header
    background-color: $color-unselection
    color: $color-body
    padding: 0
    // z-index: 9
    height: inherit
    // +bp('large')
    +res(lg)
        height: 60px
    &.no-cms
        background-color: #f9f9f9
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1)
        a
            color: #303133
        :deep()
            .el-button.moderate
                background: rgba(0, 0, 0, 0.05) !important
                border-color: rgba(0, 0, 0, 0.1) !important
                color: #303133 !important
                &:hover,
                &:focus
                    background: rgba(0, 0, 0, 0.1) !important
                    border-color: rgba(0, 0, 0, 0.15) !important
                small
                    color: rgba(0, 0, 0, 0.5) !important
            .el-dropdown-menu
                background: #fff
                border-color: #e4e7ed
                .el-dropdown-menu__item
                    color: #303133
                    &:hover
                        background: #f5f7fa
                        color: #409eff
    .textleft
        text-align: left
    .textright
        text-align: right
        // padding-top: 17px
        // padding-bottom: 17px
        height: 60px
        line-height: 60px
    a
        color: $color-body
        font-weight: bold
        text-decoration: none

    :deep() .el-button
        &.moderate
            &.el-button--default
                +button-variant($color-body, rgb(71, 71, 71), rgb(87, 87, 87))
            &.el-button--primary
                +button-variant($color-body, rgb(67, 91, 116), rgb(86, 124, 162))
            &.el-button--info
                // +button-variant($color-body, rgb(44, 77, 82), rgb(54, 108, 116))
                +button-variant($color-body, rgb(61, 61, 61), rgb(67, 67, 67))
            &.el-button--success
                +button-variant($color-body, rgb(72, 96, 59), rgb(88, 140, 61))
            &.el-button--warning
                +button-variant($color-body, rgb(100, 84, 58), rgb(161, 127, 76))
            &.el-button--danger
                +button-variant($color-body, rgb(98, 61, 61), rgb(144, 73, 73))
            small
                color: rgba(255,255,255,.6) !important
        small
            margin-right: 4px

    .header__setting
        padding-right: 15px
        :deep()
            .col
                &.sidebar-button
                    display: flex
                    align-items: center
                    padding-left: 10px
                &.inline-selection
                    padding: 0 8px
                    padding-top: 2px
                    +res(lg)
                        padding: 0 20px
                        padding-top: 2px
                    display: flex
                    align-items: center
                    justify-content: center
                    +res(lg)
                        &.header-left
                            justify-content: flex-start
                        &.header-right
                            justify-content: flex-end
                    &.grow
                        flex-grow: 1
                    .select
                        display: flex
                        flex-direction: column
                        justify-content: center
                        align-content: center
                        min-width: 0
                        max-width: 100%
                        .dynamic-width-field
                            min-width: 0
                            max-width: 100%
                            .occupy-width
                                font-size: 0.8rem
                                padding-left: 16px
                                padding-right: 34px
                                height: 0
                                z-index: -1
                                position: relative
                                white-space: nowrap
                                opacity: 0
                                max-width: 120px
                                overflow: hidden
                                +res(lg)
                                    max-width: none
                            .el-select
                                width: 0
                                min-width: 100%
                    .settings-button,
                    .clear-cache-button
                        margin-left: 10px
                    //     background: rgba(255,255,255,.1) !important
                    //     border-color: rgba(255,255,255,.1) !important
                    //     &:hover,
                    //     &:focus
                    //         background: rgba(255,255,255,.2) !important
                    //         border-color: rgba(255,255,255,.2) !important
                    //         color: #FFF !important
                    .microservice-button-container
                        margin-left: 6px
                    .el-input
                        &:hover,
                        &:focus
                            input.el-input__inner
                                background: rgba(255,255,255,.2) !important
                                border-color: rgba(255,255,255,.2) !important
                        input.el-input__inner
                            background: rgba(255,255,255,.1) !important
                            border-color: rgba(255,255,255,.1) !important
                            color: $color-body
                        i.el-select__caret.el-input__icon
                            height: 28px
                        .el-input__suffix
                            pointer-events: none
            &.header-menu
                display: flex
                .stop-mimic,
                .translation,
                .user-dropdown
                    line-height: 1
                    margin-right: 10px
                    display: flex
                    justify-content: center
                    align-items: center
                    +res(lg)
                        margin-right: 20px
                .language-trigger,
                .user-trigger
                    display: flex
                    align-items: center
                    text-decoration: none
                    color: $color-body
                    font-weight: bold
                    white-space: nowrap
                    line-height: 1
                .desktop
                    display: none
                    +res(lg)
                        display: initial
                .mobile
                    display: initial
                    +res(lg)
                        display: none
    .logo
        display: none
        padding: 6px
        a
            display: inline-block
            width: 75px
            height: auto
            img
                max-width: 100%
                max-height: 100%
        .progressive-image
            display: block
        +res(lg)
            display: flex
            flex-grow: 0
            flex-shrink: 0
            justify-content: center
            align-items: center
            background: #f9f9f9
            height: 60px
            overflow: hidden
            position: relative
            left: -1px
            padding: 0
            z-index: 999
            width: 100%
            float: left
            background: #f9f9f9
            border-right: 1px solid var(--sidebar-border)
            box-shadow: var(--sidebar-shadow)
            // +bp('large')
            +res(lg)
                width: var(--sidebar-width)

.desktop-only
    display: none !important
    +res(lg)
        display: initial !important

.mobile-only
    display: initial !important
    +res(lg)
        display: none !important

.action-menu
    margin-right: 10px
    .el-dropdown-menu__item.is-active
        color: $--color-primary
        font-weight: bold

.is-dev .el-header
    background-color: #5f2626
    &.no-cms
        background-color: #f9f9f9

.el-aside
    overflow: hidden
    background: var(--sidebar-background) !important
    position: relative
    // transform: translateX(-270px)
    width: var(--sidebar-width) !important
    margin-left: calc(-1 * var(--sidebar-width))
    z-index: 1 // z-index 0 leads to clickable tabs
    &.isLocal
        background-color: yellow !important
        &:before
            content: ""
            position: fixed
            width: 0
            height: 100%
            min-height: calc(100vh - 120px)
            background-color: rgba(yellow, 0.7)
            display: block
            top: 0
            left: 0
            z-index: 5
            transition: width 0.3s ease-in-out
        &:after
            content: ""
            position: fixed
            width: var(--sidebar-width)
            min-height: calc(100vh - 120px)
            height: 100%
            background-color: yellow
            display: block
            top: 0
            left: 0
            z-index: 0
            margin-left: calc(-1 * var(--sidebar-width))
    &:before
        content: ""
        position: fixed
        width: 0
        height: 100%
        min-height: calc(100vh - 120px)
        background-color: rgba(white, 0.7)
        display: block
        top: 0
        left: 0
        z-index: 5
        transition: width 0.3s ease-in-out
    &:after
        content: ""
        position: fixed
        width: var(--sidebar-width)
        min-height: calc(100vh - 120px)
        height: 100%
        background: var(--sidebar-background)
        border-right: 1px solid var(--sidebar-border)
        box-shadow: var(--sidebar-shadow)
        display: block
        top: 0
        left: 0
        z-index: 0
        margin-left: calc(-1 * var(--sidebar-width))
    // +bp('large')
    +res(lg)
        // transform: translateX(0)
        margin-left: 0
        &:after
            margin-left: 0

    > nav
        position: relative
        z-index: 999

.button
    &__toggle
        width: 38px
        height: 38px
        z-index: 999
        cursor: pointer
        background-color: $color-unselection
        display: block
        position: relative
        flex-shrink: 0
        // &:after
        //     content: "Menu"
        //     position: absolute
        //     left: calc(100% + 10px)
        //     top: 50%
        //     transform: translateY(-50%)
        //     text-transform: uppercase
        //     white-space: nowrap
        //     display: none
        +res(sm)
            &:after
                display: block
        span
            position: absolute
            top: 54%
            left: 25%
            display: block
            width: 50%
            height: 2px
            margin-top: -0.15rem
            background-color: $color-selection
            +noselect()
            transition: all .3s
            &:after,
            &:before
                position: absolute
                left: 0
                width: 100%
                height: 100%
                background-color: $color-selection
                content: ''
                display: block
            &:after
                transition: transform .3s
            &:before
                transition: transform .3s
                transform: translateY(-0.5rem)
            &:after
                transform: translateY(0.5rem)
        &:hover span
            transform: rotate(90deg)
            &:before
                transform: rotate(90deg)
            &:after
                transform: rotate(90deg)
    &__close
        position: absolute
        width: 38px
        height: 38px
        top: 0
        right: 0
        padding: 5px
        z-index: 9999
        cursor: pointer
        background-color: $color-unselection
        // display: block
        // +bp(medium)
        //    display: none
        +noselect()
        span
            position: absolute
            top: 48%
            left: 20%
            display: block
            width: 60%
            height: 0.15rem
            background-color: $color-selection
            +noselect()
            // transition: transform .35s
            transform: rotate(45deg)
            transition: transform .5s
            &:after,
            &:before
                position: absolute
                content: ''
                width: 100%
                height: 100%
                visibility: visible
                background: $color-selection
            &:after
                visibility: hidden

            &:before
                transform: rotate(-90deg)
                transition: transform .5s
        &:hover span
            transform: rotate(180deg)
            &:before
                transform: rotate(0deg)
            &:after
                transform: rotate(0deg)

.el-container.app-wrap:not(.sidebar-visible)
    .button__toggle
        &:hover span
            transform: none
            &:before
                opacity: 1
                transform: rotate(90deg)
            &:after
                opacity: 0
                transform: translateY(0)

.el-container.app-wrap.sidebar-visible
    .button__toggle
        &:hover span
            transform: none
            &:before,
            &:after
                opacity: 0
                transform: translateY(0)

@media only screen and (max-width: $--lg - 1)
    // The sidebar nav must scroll INTERNALLY (not stretch the flex row). It's ~840px tall, so on
    // any screen shorter than that it otherwise forces el-container.Menu — and app-wrap
    // (min-height: 100vh) — past the viewport, which scrolls the body and leaves dead space below
    // bounded pages (the messages inbox). Offset = the fixed header (60px) PLUS the nav wrapper's
    // own `.sticky-stuff` padding (15px × 2 = 30px), so the aside as a whole stays within the
    // viewport, not just the nav. Holds whether the drawer is open or closed.
    .el-aside nav
        overflow-y: auto
        height: calc(100vh - 90px)

    .el-container.app-wrap.sidebar-visible
        overflow: hidden
        max-height: 100vh
        nav
            overflow-y: auto
            height: calc(100vh - 60px)

    .Menu
        // transform: translateX(0)
        transition: transform 0.3s ease-in-out
        &__Mobile_open
            transform: translateX(var(--sidebar-width))
            .el-aside
                &:before
                    width: 100%
                    transition: width 0.3s ease-in-out
            +res(lg)
            // +bp('large')
                // transform: translateX(0)
                transform: inherit
                .el-aside
                    &:before
                        width: 0


#sticky-aside-container
    .sticky-stuff
        padding: 15px 0
        width: var(--sidebar-width)
        z-index: 1

.el-container.app-wrap.sidebar-ready
    .logo
        +res(lg)
            transition: width .25s ease

    .el-aside
        transition: width .25s ease, margin-left .25s ease

        &.isLocal
            &:after
                transition: width .25s ease, margin-left .25s ease

        &:after
            transition: width .25s ease, margin-left .25s ease

    #sticky-aside-container
        .sticky-stuff
            transition: width .25s ease

.header-left,
.header-center,
.header-right
    display: flex

.header-center
    flex-grow: 1
    justify-content: center
    align-items: center

.circle.fi
    border-radius: 50%
    padding: 0 !important
    margin-right: 4px

.left-buttons
    flex-basis: min-content
    min-width: 0
    flex-shrink: 1
    padding-left: 10px !important

.unfinished-jobs
    position: fixed
    bottom: 0
    right: 0

// The Tickets button sits in a badge, which breaks the button + button
// spacing the neighbours get from Element.
.platform-tickets-badge
    margin-left: 10px

    // The sidebar's corner badge (.menu-badge in Navigation.vue): Element's
    // own is 18px with a white ring, too heavy beside a 28px button.
    ::v-deep .el-badge__content.is-fixed
        top: 5px
        right: 7px
        min-width: 16px
        height: 16px
        padding: 0 4px
        border: 0
        border-radius: 8px
        font-size: 10px
        font-weight: 600
        line-height: 16px

.platform-tickets-badge--inline
    margin-left: 6px
</style>
